import { useMemo } from 'react'
import { convertRuleDataToTokensAndEthBalance } from '@components/Tokens/utils'
import { isEveryoneAddress } from '@components/Web3/utils'
import { getTokensWithMetadataQueryData, useTokensWithMetadata } from 'api/lib/collectionMetadata'
import { convertRuleDataV1ToV2, getRoleDetailsQueryData, useRoleDetails } from 'use-towns-client'

export type Entitlements = ReturnType<typeof useEntitlements>['data']

// returns a map of all token addresses assigned to the passed role or minter role by default, and their tokenIds if applicable
export function useEntitlements(
    spaceId: string | undefined,
    roleId: number | undefined,
    _enabled = true,
) {
    const {
        roleDetails: roleDetails,
        isLoading: isRoleDetailsLoading,
        error: roleDetailsError,
    } = useRoleDetails(spaceId ?? '', roleId, _enabled)

    const { tokens: tokensFromRuleData, ethBalance: ethBalanceFromRuleData } =
        fromRuleDataToTokensAndEthBalance(roleDetails)

    const {
        data: tokens,
        isLoading: isTokensLoading,
        isError: isTokensError,
    } = useTokensWithMetadata(tokensFromRuleData, _enabled)

    const users = useMemo(
        () => (roleDetails?.users ?? []).filter((user) => !isEveryoneAddress(user)),
        [roleDetails?.users],
    )
    const ethBalance = ethBalanceFromRuleData

    return useMemo(() => {
        return {
            data: mergedData({ tokens, users, ethBalance }),
            isLoading: isRoleDetailsLoading || isTokensLoading,
            isError: !!roleDetailsError || isTokensError,
        }
    }, [
        tokens,
        users,
        ethBalance,
        isRoleDetailsLoading,
        isTokensLoading,
        roleDetailsError,
        isTokensError,
    ])
}

function fromRuleDataToTokensAndEthBalance(
    roleDetails: ReturnType<typeof useRoleDetails>['roleDetails'],
) {
    const ruleData =
        roleDetails?.ruleData.kind === 'v2'
            ? roleDetails.ruleData.rules
            : roleDetails?.ruleData.rules
              ? convertRuleDataV1ToV2(roleDetails.ruleData.rules)
              : undefined

    const { tokens: tokensFromRuleData, ethBalance: ethBalanceFromRuleData } = ruleData
        ? convertRuleDataToTokensAndEthBalance(ruleData)
        : { tokens: [], ethBalance: '' }

    return {
        tokens: tokensFromRuleData,
        ethBalance: ethBalanceFromRuleData,
    }
}

function mergedData(args: {
    tokens: ReturnType<typeof useTokensWithMetadata>['data']
    users: string[]
    ethBalance: string
}) {
    const { tokens, users, ethBalance } = args
    return {
        tokens,
        users,
        ethBalance,
        hasEntitlements: Boolean(tokens.length > 0 || users.length > 0 || ethBalance.length > 0),
    }
}

export function getEntitlementsQueryData(spaceId: string, roleId: number) {
    const roleDetails = getRoleDetailsQueryData(spaceId, roleId)
    const users = (roleDetails?.users ?? []).filter((user) => !isEveryoneAddress(user))

    const { tokens: tokensFromRuleData, ethBalance } =
        fromRuleDataToTokensAndEthBalance(roleDetails)

    const tokens = getTokensWithMetadataQueryData(
        tokensFromRuleData.map((t) => ({
            address: t.data.address,
            chainId: t.chainId,
        })),
    )

    return mergedData({ tokens, users, ethBalance })
}
